<template>
  <div class="">
    <h1 class="title is-3">All Open Orders</h1>
    <h2 class="subtitle is-6">Last generated : {{ lastGenerated }}</h2>

    <div v-if="orders_loading || aggregate_table_loading" class="notification is-info is-light">
      Loading orders...
    </div>

    <div v-else-if="rangeSummaries.length === 0" class="notification is-warning is-light">
      No orders found.
    </div>

    <div v-else class="range-grid">
      <div
        v-for="(summary, index) in rangeSummaries"
        :key="summary.range"
        class="range-box"
        :class="{ 'is-selected': isRangeSelected(summary.range) }"
        :style="getRangeBoxStyle(summary.range, index)"
        @click="toggleSelectedRange(summary.range)"
      >
        <h3 class="title is-5 range-title">{{ summary.range }}</h3>
        <p class="range-stat">Orders: {{ summary.totalOrders }}</p>
        <p class="range-stat">Total Qty: {{ summary.totalQty }}</p>
      </div>
    </div>

    <section v-if="selectedRange" class="range-section">
      <h3 class="title is-4">{{ selectedRange }}</h3>

      <div class="box order-type-filter-box">
        <p class="has-text-weight-semibold is-size-7 mb-2">Order Type Filters</p>
        <div class="buttons are-small mb-0">
          <button
            v-for="type in availableOrderTypes"
            :key="type"
            class="button"
            :class="isOrderTypeActive(type) ? 'is-success' : 'is-light'"
            @click="toggleOrderTypeFilter(type)"
          >
            {{ type }}
          </button>
        </div>
      </div>

      <div class="tabs is-boxed is-small">
        <ul>
          <li :class="{ 'is-active': selectedRangeTab === 'summary' }">
            <a @click="selectedRangeTab = 'summary'">Summary</a>
          </li>
          <li :class="{ 'is-active': selectedRangeTab === 'orders' }">
            <a @click="selectedRangeTab = 'orders'">Orders</a>
          </li>
        </ul>
      </div>

      <div v-if="selectedRangeTab === 'summary'" class="box">
        <p><strong>Range:</strong> {{ selectedRange }}</p>
        <p><strong>Total Orders:</strong> {{ filteredSelectedRangeOrders.length }}</p>
        <p><strong>Total Qty:</strong> {{ filteredSelectedRangeTotalQty }}</p>

        <div v-if="isSocksRange" class="mt-4">
          <h4 class="title is-6">Sock Status Summary</h4>
          <table class="table is-fullwidth is-striped is-hoverable is-size-7">
            <thead>
              <tr>
                <th>Status</th>
                <th>Num Orders</th>
                <th>Total Socks</th>
                <th>% of Total Socks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in socksStatusSummary" :key="item.status">
                <td>{{ item.status }}</td>
                <td>{{ item.orders }}</td>
                <td>{{ item.garments }}</td>
                <td>{{ formatPercentage(item.garmentPercentage) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="mt-4">
          
          <table class="table is-fullwidth is-striped is-hoverable is-size-7">
            <thead>
              <tr>
                <th>Order Type</th>
                <th>Num Orders</th>
                <th>Total Garments</th>
                <th>% of Total Garments</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderTypeSummary" :key="item.orderType">
                <td>{{ item.orderType }}</td>
                <td>{{ item.orders }}</td>
                <td>{{ item.garments }}</td>
                <td>{{ formatPercentage(item.garmentPercentage) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="box">
        <div v-if="filteredSelectedRangeOrders.length === 0" class="notification is-warning is-light">
          No orders found for this range.
        </div>
        <table v-else class="table is-fullwidth is-striped is-hoverable is-size-7">
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Client Name</th>
              <th>Region</th>
              <th>Order Date</th>
              <th>Date Required</th>
              <th>Total Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in filteredSelectedRangeOrders" :key="order.orderID || order.ponumber">
              <td>{{ order.ponumber || 'N/A' }}</td>
              <td>{{ order.clientname || 'N/A' }}</td>
              <td>{{ getOrderRegion(order) }}</td>
              <td>{{ getOrderDate(order) }}</td>
              <td>{{ order.deliverydate ? formatDate(order.deliverydate) : 'N/A' }}</td>
              <td>{{ getOrderQty(order) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import OrderService from '@/services/OrderService.js';

export default {
  name: 'Overview',
  data() {
    return {
      orders: [],
      aggregate_table: [],
      accessToken: '',
      orders_loading: false,
      aggregate_table_loading: false,
      selectedRange: null,
      selectedRangeTab: 'summary',
      selectedOrderTypes: [],
    };
  },
  created() {
    this.getAccessToken().then(() => {
      this.getOrders();
      this.getAggregateTables();
    });
  },
  methods: {
    async getAccessToken() {
      this.accessToken = await this.$auth.getTokenSilently();
    },
    async getOrders() {
      this.orders_loading = true;
      OrderService.getAggregatedOpenOrders(
        this.accessToken,
       
      ).then((orders) => {
        this.$set(this, 'orders', orders);
        this.initializeOrderTypeFilters();
        this.orders_loading = false;
      });
    },
    async getAggregateTables() {
      this.aggregate_table_loading = true;
      
      try {
        const aggregateTable = await OrderService.getAggregateTable(this.accessToken);
        this.$set(this, 'aggregate_table', aggregateTable);

        
      } finally {
        this.aggregate_table_loading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    getOrderRange(order) {
      return order.Range || order.range || order.range_name || 'Unknown Range';
    },
    getOrderQty(order) {
      const qty = Number(order.total_qty);
      return Number.isFinite(qty) ? qty : 0;
    },
    getSockStatus(order) {
      const status = order.sock_status;
      if (status === null || status === undefined || String(status).trim() === '') {
        return 'Unknown';
      }

      return String(status).trim();
    },
    getOrderType(order) {
      const orderType = order.order_type || order.orderType || order.ordertype;
      if (orderType === null || orderType === undefined || String(orderType).trim() === '') {
        return 'Unknown';
      }

      return String(orderType).trim();
    },
    formatPercentage(value) {
      if (!Number.isFinite(value)) {
        return '0%';
      }

      return `${value.toFixed(1)}%`;
    },
    isRangeSelected(range) {
      return this.selectedRange === range;
    },
    toggleSelectedRange(range) {
      if (this.selectedRange === range) {
        this.selectedRange = null;
        this.selectedRangeTab = 'summary';
        this.selectedOrderTypes = [];
        return;
      }

      this.selectedRange = range;
      this.selectedRangeTab = 'summary';
      this.initializeOrderTypeFilters();
    },
    initializeOrderTypeFilters() {
      this.selectedOrderTypes = [...this.availableOrderTypes];
    },
    isOrderTypeActive(orderType) {
      return this.selectedOrderTypes.includes(orderType);
    },
    toggleOrderTypeFilter(orderType) {
      if (this.isOrderTypeActive(orderType)) {
        this.selectedOrderTypes = this.selectedOrderTypes.filter((type) => type !== orderType);
        return;
      }

      this.selectedOrderTypes = [...this.selectedOrderTypes, orderType];
    },
    getOrderRegion(order) {
      return order.region || order.client_location_name || order.clientLocationName || 'N/A';
    },
    getOrderDate(order) {
      if (order.orderdate_excel) {
        return order.orderdate_excel;
      }

      if (order.orderdate) {
        return this.formatDate(order.orderdate);
      }

      return 'N/A';
    },
    getRangeBoxStyle(range, index) {
      if (!this.isRangeSelected(range)) {
        return {
          backgroundColor: '#f0f0f0',
          borderColor: '#d1d5db',
          color: '#4b5563',
        };
      }

      let seed = index;
      for (let i = 0; i < range.length; i += 1) {
        seed += range.codePointAt(i) * (i + 1);
      }

      const hue = seed % 360;
      return {
        backgroundColor: `hsl(${hue}, 78%, 94%)`,
        borderColor: `hsl(${hue}, 50%, 70%)`,
      };
    },
    
  },
  computed: {
    lastGenerated() {
      if (!this.aggregate_table) {
        return 'N/A';
      }

      if (this.aggregate_table.last_created) {
        return this.aggregate_table.last_created;
      }

      if (Array.isArray(this.aggregate_table) && this.aggregate_table.length > 0) {
        const firstRow = this.aggregate_table[0];
        return firstRow && firstRow.last_created ? firstRow.last_created : 'N/A';
      }

      return 'N/A';
    },
    rangeSummaries() {
      const grouped = this.orders.reduce((acc, order) => {
        const range = this.getOrderRange(order);

        if (!acc[range]) {
          acc[range] = {
            range,
            totalOrders: 0,
            totalQty: 0,
          };
        }

        acc[range].totalOrders += 1;
        acc[range].totalQty += this.getOrderQty(order);
        return acc;
      }, {});

      return Object.values(grouped).sort((a, b) => a.range.localeCompare(b.range));
    },
    selectedRangeOrders() {
      if (!this.selectedRange) {
        return [];
      }

      return this.orders.filter((order) => this.getOrderRange(order) === this.selectedRange);
    },
    availableOrderTypes() {
      const uniqueTypes = new Set(this.selectedRangeOrders.map((order) => this.getOrderType(order)));
      return Array.from(uniqueTypes).sort((a, b) => a.localeCompare(b));
    },
    filteredSelectedRangeOrders() {
      if (this.selectedOrderTypes.length === 0) {
        return [];
      }

      return this.selectedRangeOrders.filter((order) =>
        this.selectedOrderTypes.includes(this.getOrderType(order))
      );
    },
    filteredSelectedRangeTotalQty() {
      return this.filteredSelectedRangeOrders.reduce((total, order) => total + this.getOrderQty(order), 0);
    },
    selectedRangeTotalQty() {
      return this.selectedRangeOrders.reduce((total, order) => total + this.getOrderQty(order), 0);
    },
    isSocksRange() {
      return typeof this.selectedRange === 'string' && this.selectedRange.toLowerCase() === 'socks';
    },
    socksStatusSummary() {
      if (!this.isSocksRange) {
        return [];
      }

      const grouped = this.filteredSelectedRangeOrders.reduce((acc, order) => {
        const status = this.getSockStatus(order);

        if (!acc[status]) {
          acc[status] = {
            status,
            orders: 0,
            garments: 0,
          };
        }

        acc[status].orders += 1;
        acc[status].garments += this.getOrderQty(order);
        return acc;
      }, {});

      const totalGarments = this.filteredSelectedRangeTotalQty;
      return Object.values(grouped)
        .map((item) => ({
          ...item,
          garmentPercentage: totalGarments > 0 ? (item.garments / totalGarments) * 100 : 0,
        }))
        .sort((a, b) => a.status.localeCompare(b.status));
    },
    orderTypeSummary() {
      if (this.isSocksRange) {
        return [];
      }

      const grouped = this.filteredSelectedRangeOrders.reduce((acc, order) => {
        const orderType = this.getOrderType(order);

        if (!acc[orderType]) {
          acc[orderType] = {
            orderType,
            orders: 0,
            garments: 0,
          };
        }

        acc[orderType].orders += 1;
        acc[orderType].garments += this.getOrderQty(order);
        return acc;
      }, {});

      const totalGarments = this.filteredSelectedRangeTotalQty;
      return Object.values(grouped)
        .map((item) => ({
          ...item,
          garmentPercentage: totalGarments > 0 ? (item.garments / totalGarments) * 100 : 0,
        }))
        .sort((a, b) => a.orderType.localeCompare(b.orderType));
    },
  },
  mounted() {
    // Component mounted
  },
};
</script>

<style scoped>
.range-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.range-box {
  border: 1px solid;
  border-radius: 10px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.range-box:hover {
  transform: translateY(-1px);
}

.range-box.is-selected {
  box-shadow: 0 0 0 2px rgba(20, 20, 20, 0.12);
}

.range-title {
  margin-bottom: 8px !important;
}

.range-stat {
  font-size: 0.95rem;
  margin: 0;
}

.range-section {
  margin-top: 20px;
}

.order-type-filter-box {
  padding: 12px;
}
</style>

